import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { productApi, categoryApi } from '../api';
import { Product, Category } from '../types';
import ProductCard from '../components/product/ProductCard';
import Loading from '../components/common/Loading';
import { localized } from '../utils/i18nField';

const ProductListPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);

  const categoryId = searchParams.get('categoryId') ? Number(searchParams.get('categoryId')) : undefined;
  const keyword = searchParams.get('keyword') || undefined;
  const sortType = searchParams.get('sortType') || undefined;
  const onSale = searchParams.get('onSale') === 'true' || undefined;
  const page = Number(searchParams.get('page') || 0);

  useEffect(() => {
    categoryApi.getAll().then(r => setCategories(r.data.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    productApi.getList({ categoryId, keyword, sortType, onSale, page, size: 20 })
      .then(r => {
        setProducts(r.data.data.content);
        setTotalPages(r.data.data.totalPages);
      })
      .finally(() => setLoading(false));
  }, [categoryId, keyword, sortType, onSale, page]);

  const setParam = (key: string, value: string | undefined) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete('page');
    setSearchParams(next);
  };

  const sortLabel = sortType === 'best' ? t('productList.best') : sortType === 'new' ? t('productList.newArrival') : t('productList.all');
  const currentCategory = categoryId ? categories.find(c => c.id === categoryId) : undefined;

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <aside style={styles.sidebar}>
          <h3 style={styles.sideTitle}>{t('home.category')}</h3>
          <ul style={styles.catList}>
            <li
              style={{ ...styles.catItem, ...(categoryId === undefined ? styles.catActive : {}) }}
              onClick={() => setParam('categoryId', undefined)}
            >
              {t('productList.all')}
            </li>
            {categories.map(cat => (
              <li
                key={cat.id}
                style={{ ...styles.catItem, ...(categoryId === cat.id ? styles.catActive : {}) }}
                onClick={() => setParam('categoryId', String(cat.id))}
              >
                {localized(cat.name, cat.nameEn, i18n.language)}
              </li>
            ))}
          </ul>

          <h3 style={{ ...styles.sideTitle, marginTop: 32 }}>{t('productList.sort')}</h3>
          <ul style={styles.catList}>
            {[
              { label: t('productList.latest'), value: undefined },
              { label: t('productList.best'), value: 'best' },
              { label: t('productList.newArrival'), value: 'new' },
            ].map(s => (
              <li
                key={s.label}
                style={{ ...styles.catItem, ...(sortType === s.value ? styles.catActive : {}) }}
                onClick={() => setParam('sortType', s.value)}
              >
                {s.label}
              </li>
            ))}
          </ul>
        </aside>

        <main style={styles.main}>
          <div style={styles.topBar}>
            <h2 style={styles.pageTitle}>
              {keyword
                ? `"${keyword}" ${t('productList.searchResults')}`
                : currentCategory
                  ? localized(currentCategory.name, currentCategory.nameEn, i18n.language)
                  : sortLabel}
            </h2>
            <span style={styles.count}>{products.length}{t('productList.itemCount')}</span>
          </div>

          {loading ? <Loading /> : (
            <>
              {products.length === 0 ? (
                <div style={styles.empty}>
                  <p>{t('productList.noResults')}</p>
                </div>
              ) : (
                <div style={styles.grid}>
                  {products.map(p => <ProductCard key={p.id} product={p} />)}
                </div>
              )}

              {totalPages > 1 && (
                <div style={styles.pagination}>
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button
                      key={i}
                      style={{ ...styles.pageBtn, ...(page === i ? styles.pageBtnActive : {}) }}
                      onClick={() => setParam('page', String(i))}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: { maxWidth: 1200, margin: '40px auto', padding: '0 20px' },
  container: { display: 'flex', gap: 32 },
  sidebar: { width: 200, flexShrink: 0 },
  sideTitle: { fontSize: 14, fontWeight: 700, color: '#333', marginBottom: 12 },
  catList: { listStyle: 'none' },
  catItem: {
    padding: '8px 12px', borderRadius: 6, fontSize: 13, color: '#666',
    cursor: 'pointer', transition: 'all 0.15s',
  },
  catActive: { background: '#ece3f7', color: '#b39ddb', fontWeight: 600 },
  main: { flex: 1 },
  topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 },
  pageTitle: { fontSize: 20, fontWeight: 700, color: '#333' },
  count: { fontSize: 13, color: '#888' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20 },
  empty: { textAlign: 'center', padding: '80px 0', color: '#bbb', fontSize: 16 },
  pagination: { display: 'flex', justifyContent: 'center', gap: 8, marginTop: 40 },
  pageBtn: {
    width: 36, height: 36, borderRadius: 6, border: '1px solid #e8e8e8',
    background: '#fff', fontSize: 13, cursor: 'pointer', color: '#555',
  },
  pageBtnActive: { background: '#b39ddb', color: '#fff', borderColor: '#b39ddb' },
};

export default ProductListPage;
